/**
 * Embed-attribute parsing for the events carousel.
 *
 * Woodside's WordPress embeds write one shared `data-params` convention across
 * every widget on the site, borrowed from the announcements widget: a
 * comma-separated list of `Key=Value` pairs with an optional `@` prefix.
 *
 *   data-params="MinistryID=113,campus=troy"
 *   data-params="@Search=celebrate recovery"
 *
 * Query-string style (`&`) and the MP Event Finder's own field names
 * (`congregationid`, `ministryid`, `programid`, `keyword`) are accepted too, so
 * a filter that works on the events page can be pasted here unchanged.
 */
export interface EmbedParams {
  campus?: string;
  ministryId?: number;
  programId?: number;
  search?: string;
}

export function parseDataParams(raw: string | null | undefined): EmbedParams {
  if (!raw) return {};

  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // Leave it as-is if it isn't valid percent-encoding.
  }

  const out: EmbedParams = {};

  for (const pair of decoded.split(/[,&]/)) {
    const eq = pair.indexOf('=');
    if (eq === -1) continue;

    const key = pair.slice(0, eq).trim().replace(/^@/, '').toLowerCase();
    const value = pair.slice(eq + 1).trim();
    if (!value) continue; // `campus=` means "unset", not "match empty"

    switch (key) {
      case 'campus':
      case 'congregationid':
      case 'congregation_id':
        out.campus = value;
        break;
      case 'ministry':
      case 'ministryid':
      case 'ministry_id':
        if (/^\d+$/.test(value)) out.ministryId = Number(value);
        break;
      case 'program':
      case 'programid':
      case 'program_id':
        if (/^\d+$/.test(value)) out.programId = Number(value);
        break;
      case 'search':
      case 'keyword':
        out.search = value;
        break;
      default:
        break;
    }
  }

  return out;
}

/** Treat a missing or blank data attribute as unset. */
export function attr(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}
